import type { ReactNode } from 'react'
import { useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import clsx from 'clsx'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import useHorizontalScrollIntoView from 'hooks/useHorizontalScrollIntoView'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import StickyWithStatus from 'components/Common/StickyWithStatus'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import LayoutContainer from 'components/Common/LayoutContainer'

const TabOption = styled(Clickable)`
  height: ${rem(48)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${rem(16)};
  min-width: ${rem(96)};

  ${mediaQueryUp.tablet} {
    height: ${rem(56)};
  }
`

const TabOptionSelectedLine = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  height: ${rem(3)};
  width: 100%;
  background-color: ${props => props.theme.palette.brand.primary.normal};
  display: none;

  &.active {
    display: block;
  }
`

const Wrapper = styled(StickyWithStatus)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: 1;
`

const TabOptionText = styled(BodyText)`
  transition: color 0.2s;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`

interface ContentFields {
  id: string
  label: string | ReactNode
}
interface Props {
  contentFields: Array<ContentFields>
  onClick?: (id: string) => void
  selectedHeader?: string
  ref?: React.Ref<HTMLDivElement | null>
}

function NavigableOfferContentBar(props: Props) {
  const { contentFields, onClick = noop, selectedHeader, ref } = props
  const [scroller, contentElements] = useHorizontalScrollIntoView(selectedHeader)
  const [isStuck, setIsStuck] = useState(false)
  useAppHeaderObserver(scroller, isStuck ? 'sticky' : 'static')

  return <Wrapper onStuckChange={setIsStuck} ref={ref}>
    <LayoutContainer>
      <HorizontalContainerWithFadedOverflow role="menubar" ref={scroller}>
        {contentFields.map(contentField => {
          return <TabOption
            ref={(element) => { contentElements.current[contentField.id] = element as HTMLButtonElement }}
            key={contentField.id}
            onClick={() => onClick(contentField.id)}
            role="menuitem"
          >
            <TabOptionText
              wrap="no-wrap"
              colour={contentField.id === selectedHeader ? 'primary' : undefined}
              variant="medium"
            >
              {contentField.label}
              <TabOptionSelectedLine className={clsx({ active: contentField.id === selectedHeader })} />
            </TabOptionText>
          </TabOption>
        })}
      </HorizontalContainerWithFadedOverflow>
    </LayoutContainer>
  </Wrapper>
}

export default NavigableOfferContentBar
